import { ArrowUp, ArrowDown } from '@tamagui/lucide-icons';
import React from 'react';
import Svg, { Path } from 'react-native-svg';
import { View, XStack, Text, ZStack, ViewProps, Stack } from 'tamagui';
import { tokens } from 'tamagui-config';

import { GlowBackground } from '../../../components/common/GlowBackground';

function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return ['M', start.x, start.y, 'A', r, r, 0, largeArcFlag, 0, end.x, end.y].join(' ');
}
function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  angle: number,
): { x: number; y: number } {
  const a = ((angle - 90) * Math.PI) / 180.0;
  return {
    x: cx + r * Math.cos(a),
    y: cy + r * Math.sin(a),
  };
}

enum Colors {
  RED,
  YELLOW,
  GREEN,
}

const SCALE = [
  Colors.RED,
  Colors.RED,
  Colors.YELLOW,
  Colors.YELLOW,
  Colors.YELLOW,
  Colors.YELLOW,
  Colors.GREEN,
  Colors.GREEN,
  Colors.GREEN,
];

function getArcSegments(value: number, maxValue: number): [number, number, string][] {
  const startAngle = 240;
  const endAngle = 480;
  const gap = 12;

  const colors = SCALE.map((color, index) => {
    const step = maxValue / SCALE.length;
    switch (color) {
      case Colors.RED:
        return value > index * step ? tokens.color.energyRed : tokens.color.energyRedAlpha20;
      case Colors.YELLOW:
        return value > index * step ? tokens.color.warning : tokens.color.warningAlpha20;
      case Colors.GREEN:
      default:
        return value > index * step ? tokens.color.successGreen : tokens.color.successGreenAlpha20;
    }
  });

  const numSegments = colors.length;
  const totalAngle = endAngle - startAngle;
  const totalGap = gap * (numSegments - 1);
  const segmentAngle = (totalAngle - totalGap) / numSegments;

  const segments: [number, number, string][] = [];

  for (let i = 0; i < numSegments; i++) {
    const start = startAngle + i * (segmentAngle + gap);
    const end = start + segmentAngle;
    segments.push([start, end, colors[i]]);
  }

  return segments;
}

function getSegmentColor(value: number, maxValue: number) {
  const step = maxValue / SCALE.length;
  let color: Colors = SCALE[0];
  for (let i = 0; i < SCALE.length; i++) {
    if (value > i * step) {
      color = SCALE[i];
    } else {
      break;
    }
  }
  switch (color) {
    case Colors.RED:
      return tokens.color.energyRed;
    case Colors.YELLOW:
      return tokens.color.warning;
    case Colors.GREEN:
    default:
      return tokens.color.successGreen;
  }
}

const Label = ({
  color,
  backgroundColor,
  text,
  props,
}: {
  color: string;
  backgroundColor: string;
  text: string;
  props: ViewProps;
}) => (
  <View
    backgroundColor={backgroundColor}
    borderRadius={'$5'}
    paddingHorizontal={'$2.5'}
    paddingVertical={'$0.5'}
    marginEnd={'$3'}
    {...props}>
    <Text color={color} fontWeight="$5" fontSize={'$5.5'}>
      {text}
    </Text>
  </View>
);

interface SEERGaugeProps {
  value: number;
  minValue: number;
  midValue: number;
  maxValue: number;
  size?: number;
  valueColor?: string;
  hvacRunning?: boolean;
  valueDifference?: number;
}

export const SEERGauge: React.FC<SEERGaugeProps> = ({
  value,
  minValue,
  midValue,
  maxValue,
  size = 170,
  hvacRunning = true,
  valueDifference,
}) => {
  const strokeWidth = 14;
  const arcRadius = (size - strokeWidth) / 2;
  const center = size / 2;
  const valueColor = getSegmentColor(value, maxValue);

  const renderDifferenceIndicator = () => {
    if (valueDifference === undefined) return null;

    const isPositive = valueDifference >= 0;
    const color = isPositive ? tokens.color.successGreen : tokens.color.energyRed;
    const TrendIcon = isPositive ? ArrowUp : ArrowDown;

    return (
      <XStack
        position="absolute"
        top={size / 2 + 20}
        alignSelf="center"
        justifyContent="center"
        alignItems="center"
        paddingHorizontal="$0.5"
        paddingVertical="$0.5"
        space="$0.5">
        <TrendIcon strokeWidth={2} size={'$2'} color={color} />
        <Text fontSize={'$8'} fontWeight="$6" color={color}>
          {Math.abs(valueDifference).toFixed(1)}
        </Text>
      </XStack>
    );
  };
  return (
    <Stack>
      <GlowBackground color={valueColor} />
      <ZStack style={{ width: size, height: size * 0.85 }}>
        <Svg width={size} height={size}>
          {getArcSegments(value, maxValue).map(([start, end, color], i) => (
            <Path
              key={i}
              d={describeArc(center, center, arcRadius, start, end)}
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              fill="none"
            />
          ))}
        </Svg>
        {hvacRunning ? (
          <>
            <Text
              position="absolute"
              top={size / 2 - 55}
              left={0}
              right={0}
              textAlign="center"
              fontFamily={'$heading'}
              fontSize={'$9.5'}
              fontWeight="$9.5"
              color={valueColor}>
              {value < 1 ? '-' : value.toFixed(1)}
            </Text>
            {renderDifferenceIndicator()}
          </>
        ) : (
          <XStack
            position="absolute"
            top={size / 2 - 25}
            left={0}
            right={0}
            justifyContent="center"
            alignItems="center"
            gap="$2">
            <View
              width={16}
              height={16}
              borderRadius={8}
              borderWidth={2}
              borderColor={tokens.color.successGreen}
              backgroundColor="transparent"
            />
            <Text
              fontFamily={'$heading'}
              fontSize={'$4'}
              fontWeight="$6"
              color={tokens.color.white12}>
              A/C OFF
            </Text>
          </XStack>
        )}
        <Label
          color={tokens.color.warning}
          backgroundColor={tokens.color.warningAlpha20}
          text={`${midValue}`}
          props={{
            position: 'absolute',
            top: '$5',
            alignSelf: 'center',
          }}
        />
        <XStack
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          width="100%"
          justifyContent="space-between"
          paddingHorizontal={'$7'}>
          <Label
            color={tokens.color.energyRed}
            backgroundColor={tokens.color.energyRedAlpha20}
            text={`${minValue}`}
            props={{
              marginStart: '$3',
            }}
          />
          <Label
            color={tokens.color.successGreen}
            backgroundColor={tokens.color.successGreenAlpha20}
            text={`${maxValue}`}
            props={{
              marginEnd: '$3',
            }}
          />
        </XStack>
      </ZStack>
    </Stack>
  );
};
